/**
 * Связь и оплата.
 *
 * Здесь два поля, которые легко перепутать, поэтому они разведены и подписаны
 * прямо в интерфейсе:
 *
 *   — телефон для связи. Отдельный от того, что пришёл из Telegram: тот
 *     служит идентификацией и меняться не должен, а по этому с исполнителем
 *     связывается клиент.
 *   — карта для ВЫПЛАТ. По ней с исполнителем рассчитывается площадка.
 *     Клиенту она не показывается никогда — клиент платит наличными. Об этом
 *     сказано подсказкой под полем, иначе кто-нибудь решит, что сюда придут
 *     переводы от заказчиков, и будет ждать их напрасно.
 */
import { useState } from 'react';
import type { CurrentUser, Vendor } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { secondaryButton } from './courierUi';
import { Field, bottomBarStyle, errorTextStyle, hintStyle, pageWithBottomBar } from './vendorUi';

interface Props {
  user: CurrentUser;
  vendor: Vendor;
  onVendor: (v: Vendor) => void;
  onBack: () => void;
}

export function VendorPayout({ user, vendor, onVendor, onBack }: Props) {
  const t = makeT(user.locale);

  const [displayName, setDisplayName] = useState(vendor.display_name);
  const [phone, setPhone] = useState(vendor.contact_phone ?? '');
  const [card, setCard] = useState(vendor.payout_card_number ?? '');
  const [holder, setHolder] = useState(vendor.payout_card_holder ?? '');
  const [capacity, setCapacity] = useState(String(vendor.capacity_per_slot));

  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true);
    setFields({});
    setSaved(false);

    // Пустое поле означает «убрать», а не «не трогать»: null сервер понимает,
    // пустую строку — нет, её отвергнет формат.
    const body = {
      display_name: displayName.trim(),
      contact_phone: phone.trim() || null,
      payout_card_number: card.replace(/\s/g, '') || null,
      payout_card_holder: holder.trim() || null,
      capacity_per_slot: Number(capacity) || 1,
    };

    try {
      const updated = await api.patch<Vendor>('/api/vendor', body);
      onVendor(updated);
      haptic('success');
      setSaved(true);
    } catch (err) {
      haptic('error');
      setFields(err instanceof ApiError ? (err.fields ?? { _: err.message }) : { _: t('actionFailed') });
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="page" style={pageWithBottomBar}>
      <h1 className="h1">{t('payoutTitle')}</h1>

      <div style={{ display: 'grid', gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}>
        <Field
          id="v-name"
          label={t('shopNameLabel')}
          hint={t('shopNameHint')}
          error={fields.display_name}
        >
          {(p) => (
            <input
              {...p}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
            />
          )}
        </Field>

        <Field
          id="v-phone"
          label={t('payoutPhoneLabel')}
          hint={t('payoutPhoneHint')}
          error={fields.contact_phone}
        >
          {(p) => (
            <input
              {...p}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              placeholder="+998"
              autoComplete="tel"
            />
          )}
        </Field>

        <Field
          id="v-card"
          label={t('payoutCardLabel')}
          hint={t('payoutCardHint')}
          error={fields.payout_card_number}
        >
          {(p) => (
            <input
              {...p}
              value={card}
              onChange={(e) => setCard(e.target.value.replace(/[^\d]/g, '').slice(0, 16))}
              inputMode="numeric"
              autoComplete="off"
            />
          )}
        </Field>

        <Field id="v-holder" label={t('payoutHolderLabel')} error={fields.payout_card_holder}>
          {(p) => (
            <input
              {...p}
              value={holder}
              onChange={(e) => setHolder(e.target.value)}
              maxLength={120}
              autoComplete="off"
            />
          )}
        </Field>

        <Field
          id="v-capacity"
          label={t('shopCapacityLabel')}
          hint={t('shopCapacityHint')}
          error={fields.capacity_per_slot}
        >
          {(p) => (
            <input
              {...p}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
              inputMode="numeric"
            />
          )}
        </Field>

        {fields._ && (
          <p role="alert" style={errorTextStyle}>
            {fields._}
          </p>
        )}
        {saved && !fields._ && (
          <p role="status" style={{ ...hintStyle, color: 'var(--c-primary-text)', margin: 0 }}>
            {t('shopSaved')}
          </p>
        )}
      </div>

      <div style={bottomBarStyle}>
        <button
          type="button"
          className="btn-primary"
          disabled={saving || !displayName.trim()}
          data-loading={saving ? 'true' : undefined}
          onClick={() => void save()}
        >
          {t('payoutSave')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('shopTitle')}
        </button>
      </div>
    </main>
  );
}
